/**
 * Puts the contact details a parent already saved back onto the form.
 *
 * The page is built from the TASS parent history every time, so a parent coming back to finish
 * would otherwise see the school's values again and overwrite their own on the next save. The
 * TASS snapshot taken on load stays as it is — that is what the change comparison is measured
 * against — and only the fields on screen are replaced.
 *
 * Mobile numbers are stored with their "(M)" / "(F)" prefix, the same shape TASS sends.
 */

const text = (value) => (value == null ? '' : String(value));

const splitMobile = (value, fallbackInitial) => {
    const stored = text(value);

    return {
        initial: stored.match(/\(.*?\)/)?.[0] || fallbackInitial,
        number: stored.replace(/\(.*?\)/g, '').replace(/\s/g, ''),
    };
};

export const formValuesFromSavedContact = (saved) => {
    if (!saved) {
        return null;
    }

    const mobile1 = splitMobile(saved.Number1, '(M)');
    const mobile2 = splitMobile(saved.Number2, '(F)');

    const values = {
        Email1: text(saved.Email1),
        Email2: text(saved.Email2),
        Number1: mobile1.number,
        Number2: mobile2.number,
        Address1: text(saved.Address1),
        Address2: text(saved.Address2),
        Address3: text(saved.Address3),
        Town: text(saved.Town),
        PostCode: text(saved.PostCode),
        State: text(saved.State),
    };

    // A row of blanks is not something a parent saved on purpose, and applying it would wipe the
    // TASS values off the form.
    if (Object.values(values).every((value) => value === '')) {
        return null;
    }

    return {
        values,
        numberInitial1: mobile1.initial,
        numberInitial2: mobile2.initial,
    };
};

/**
 * Puts a parent's saved medical answers back on the student records loaded from TASS.
 *
 * Only the answers they can change are overlaid — enrolment status, campus, photo and the rest of
 * the record stay as loaded. A student with nothing saved keeps the TASS values.
 */
const MEDICAL_FIELDS = [
    'immunisation_status',
    'ambulance_cover',
    'private_health_insurance',
    'allergy',
    'medicare_number',
    'medicare_expiry',
];

// Yes/no answers, where false is an answer the parent gave rather than one they left blank.
const MEDICAL_FLAGS = ['istemporaryVisa', 'hasCustomMedicare'];

export const mergeSavedStudentMedical = (records, saved) => {
    if (!Array.isArray(records) || !Array.isArray(saved) || saved.length === 0) {
        return records;
    }

    const savedByCode = new Map(
        saved.filter((row) => row && row.stud_code).map((row) => [String(row.stud_code).trim(), row])
    );

    return records.map((record) => {
        const code = String(record?.student_code ?? record?.stud_code ?? '').trim();
        const savedRow = savedByCode.get(code);
        if (!savedRow) return record;

        const merged = { ...record };
        MEDICAL_FIELDS.forEach((field) => {
            // An empty answer is one the parent has not given, not one they cleared.
            if (savedRow[field] !== '' && savedRow[field] != null) {
                merged[field] = savedRow[field];
            }
        });

        MEDICAL_FLAGS.forEach((field) => {
            if (typeof savedRow[field] === 'boolean') {
                merged[field] = savedRow[field];
            }
        });

        // The form reads either spelling of the temporary visa answer, so keep them together.
        if (typeof savedRow.istemporaryVisa === 'boolean') {
            merged.temporary_visa = savedRow.istemporaryVisa;
        }

        return merged;
    });
};
